import React, { type ReactNode } from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import { StyleSheet } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { pointerEventsStyles, Button, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const PartnerLineItemFloatingButton = (props: Props) => {
  const { accessibilityLabel, children, disabled, isLoading, onPress } = props;

  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();

  // ─── Styles ──────────────────────────────────────────────────────────

  const safeAreaStyles = useStyle(
    () => ({ paddingBottom: Math.max(safeAreaBottomInset, theme.spacing['6']) }),
    [safeAreaBottomInset],
  );
  const containerStyles = [styles.container, safeAreaStyles];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View
      entering={FadeIn.duration(theme.transitions.base).delay(650)}
      style={[styles.outerContainer, pointerEventsStyles['box-none']]}
    >
      <LinearGradient
        colors={GRADIENT_COLORS}
        end={GRADIENT_END}
        locations={GRADIENT_LOCATIONS}
        start={GRADIENT_START}
        style={[containerStyles, pointerEventsStyles['box-none']]}
      >
        <Button
          size="large-wide"
          accessibilityLabel={accessibilityLabel}
          disabled={disabled}
          isLoading={isLoading}
          onPress={onPress}
          style={styles.button}
        >
          {children}
        </Button>
      </LinearGradient>
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const GRADIENT_COLORS = ['rgba(248, 247, 240, 0)', 'rgba(248, 247, 240, 0.7)'] as const;
const GRADIENT_LOCATIONS = [0.25, 1] as const;
const GRADIENT_START = { x: 0, y: 0 } as const;
const GRADIENT_END = { x: 0, y: 1 } as const;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
  },
  container: {
    paddingTop: theme.spacing['10'],
    paddingHorizontal: theme.spacing['6'],
  },
  button: {
    width: '100%',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  accessibilityLabel: string;
  children: ReactNode;
  disabled: boolean;
  isLoading: boolean;
  onPress: () => void;
};
